/**
 * Section « Ville » (D§3) — terrain et cadrage.
 *
 * Elle répond à deux questions que le joueur se pose dès la première minute et
 * auxquelles l'interface ne répondait nulle part : **comment agrandir la carte**, et
 * **comment recadrer la vue**. Les commandes existaient, mais derrière `?debug` : elles
 * n'étaient donc accessibles à personne.
 *
 * L'extension de terrain est un ACHAT en pièces (§6), pas un bouton technique. Son coût
 * croît fortement, et la carte est bornée : une grille sans fin tue la lisibilité de la
 * ville bien avant de manquer de place.
 */

import { ECONOMY, GEM_SPENDING } from '@/config/balance';
import { nextLandCost } from '@/core/economy';
import { activeIsland, currentProduction } from '@/core/gameState';
import type { CitySnapshot } from '@/core/gameState';
import { n, t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import type { RemoteCity } from '@/net/sync';
import { createButton, createIconButton, createPanel } from './components';
import { createTabbedSection } from './tabbedSection';
import type { ButtonHandle } from './components';
import { icon } from './icons';

export interface CitySectionCallbacks {
  readonly onBuyLand: () => void;
  /** Retirer le bitume qui ne dessert plus rien (ROADMAP 5.2). */
  readonly onCleanRoads: () => void;
  /**
   * Combien de cases le nettoyage rendrait, à l'instant présent.
   *
   * La section ne le calcule PAS : le compte vient de `core/roadCleanup`, qui simule le
   * geste au lieu de l'estimer. Une interface qui refait le calcul finit par annoncer un
   * chiffre différent de ce que l'action produit (règle 22).
   */
  readonly orphanRoads: () => number;
  /**
   * Acheter l'extension en GEMMES, depuis l'écran où le manque de place se constate
   * (G§4, B1). Le même geste que dans la boutique, au moment où il a du sens.
   */
  readonly onBuyLandWithGems?: () => void;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onRecenter: () => void;
  /**
   * Le joueur renomme sa ville. Rend le nouveau nom accepté, ou `null` si le serveur
   * a refusé — l'écran repeint alors l'ancien plutôt que de mentir.
   */
  readonly onRename?: (name: string) => Promise<string | null>;
}

export interface CitySectionHandle {
  readonly el: HTMLElement;
  update(snapshot: CitySnapshot): void;
  /** La ville distante est connue (ou renommée) : l'identité se remplit. */
  setCity(city: RemoteCity | null): void;
}

function stat(label: string, value: string): { el: HTMLElement; set(v: string): void } {
  const el = document.createElement('div');
  el.className = 'city-stat';

  const key = document.createElement('span');
  key.className = 'city-stat__label';
  key.textContent = label;

  const val = document.createElement('b');
  val.className = 'city-stat__value';
  val.textContent = value;

  el.append(key, val);
  return {
    el,
    set(v) {
      val.textContent = v;
    },
  };
}

export function createCitySection(callbacks: CitySectionCallbacks): CitySectionHandle {
  const el = document.createElement('div');
  el.className = 'city-section';

  /*
    ── Identité — le nom de la ville OUVRE la section (D§4) ──────────────────

    Le nom et l'adresse publique existaient depuis la Phase 2 et ne s'affichaient
    nulle part ailleurs que dans l'URL du panneau Social. C'est l'identité du joueur :
    elle se lit ici en premier, et se change ici — le renommage est une des deux seules
    écritures client autorisées par la base (§5).
  */
  const cityName = document.createElement('h2');
  cityName.className = 'city-identity__name';
  cityName.textContent = t('city.defaultName');

  const citySlug = document.createElement('p');
  citySlug.className = 'city-identity__slug';
  citySlug.hidden = true;

  const renameInput = document.createElement('input');
  renameInput.className = 'city-identity__input';
  renameInput.maxLength = 40;
  renameInput.setAttribute('aria-label', t('city.renameLabel'));
  renameInput.hidden = true;

  const renameButton: ButtonHandle = createButton({
    label: t('city.rename'),
    variant: 'ghost',
    size: 'sm',
    onClick: () => {
      if (renameInput.hidden) {
        // Passage en édition : le champ reprend le nom courant, le bouton devient
        // « Enregistrer ». Un seul bouton à deux temps — pas deux boutons à confondre.
        renameInput.value = cityName.textContent;
        renameInput.hidden = false;
        cityName.hidden = true;
        renameButton.setLabel(t('city.renameSave'));
        renameInput.focus();
        renameInput.select();
        return;
      }
      const wanted = renameInput.value.trim();
      renameInput.hidden = true;
      cityName.hidden = false;
      renameButton.setLabel(t('city.rename'));
      if (wanted === '' || wanted === cityName.textContent || callbacks.onRename === undefined) {
        return;
      }
      renameButton.setDisabled(true);
      void callbacks.onRename(wanted).then((accepted) => {
        renameButton.setDisabled(false);
        // Le serveur fait foi : on n'affiche que ce qu'il a accepté.
        if (accepted !== null) cityName.textContent = accepted;
      });
    },
  });
  renameButton.el.classList.add('city-identity__rename');
  renameButton.setDisabled(true);

  renameInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') renameButton.el.click();
    if (event.key === 'Escape') {
      renameInput.hidden = true;
      cityName.hidden = false;
      renameButton.setLabel(t('city.rename'));
    }
  });

  const identityHead = document.createElement('div');
  identityHead.className = 'city-identity__head';
  identityHead.append(cityName, renameInput, renameButton.el);

  const identityPanel = createPanel({
    elevation: 'raised',
    children: [identityHead, citySlug],
  });
  identityPanel.el.classList.add('city-identity');

  /* ── Statistiques — le portrait chiffré de la ville ───────────────────── */
  const statBuildings = stat(t('city.statBuildings'), '—');
  const statRoads = stat(t('city.statRoads'), '—');
  const statProduction = stat(t('city.statProduction'), '—');
  const statVisitors = stat(t('city.statVisitors'), '—');
  const statEra = stat(t('city.statEra'), '—');

  const statsGrid = document.createElement('div');
  statsGrid.className = 'city-stats';
  statsGrid.append(statBuildings.el, statRoads.el, statProduction.el, statVisitors.el, statEra.el);

  const statsPanel = createPanel({
    children: [
      (() => {
        const head = document.createElement('h3');
        head.className = 'city-section__title';
        head.append(icon('book'), t('city.statsTitle'));
        return head;
      })(),
      statsGrid,
    ],
  });

  /* ── Terrain ──────────────────────────────────────────────────────────── */
  const size = stat(t('city.gridSize'), '—');
  const expansions = stat(t('city.expansions'), '—');

  const buyLand: ButtonHandle = createButton({
    label: t('city.buyLand'),
    variant: 'primary',
    icon: 'plus',
    onClick: callbacks.onBuyLand,
  });
  buyLand.el.classList.add('city-buy');

  const landPrice = document.createElement('span');
  landPrice.className = 'city-price';

  /*
    ── LES DEUX MONNAIES CÔTE À CÔTE (G§4, B1) ───────────────────────────────

    Le prix en pièces croît d'un facteur 3,2 par extension ; celui en gemmes est FIXE.
    Les afficher ensemble rend le raccourci lisible sans calcul : au début le prix en
    pièces est dérisoire et personne ne dépense de gemmes, en fin d'ère il explose et
    45 gemmes deviennent évidentes. C'est l'offre qui se qualifie elle-même — aucun
    argumentaire à écrire.
  */
  const buyLandGems: ButtonHandle = createButton({
    label: t('gems.landCost', { value: n(GEM_SPENDING.land.cost) }),
    variant: 'secondary',
    icon: 'gem',
    onClick: () => {
      callbacks.onBuyLandWithGems?.();
    },
  });
  buyLandGems.el.classList.add('city-buy--gems');

  /*
    ── LE BITUME ORPHELIN (ROADMAP 5.2) ──────────────────────────────────────

    Il vit dans le panneau TERRAIN et pas ailleurs, parce que c'est du terrain qu'il
    rend : le réseau se trace tout seul et ne se rétracte jamais, si bien qu'après
    quelques ventes il reste des rues qui ne desservent personne — et `canPlace` refuse
    de bâtir dessus.

    Le bouton n'existe QUE lorsqu'il a quelque chose à faire, et il annonce le nombre
    exact de cases qu'il va rendre. Un bouton permanent affichant « 0 » demanderait au
    joueur d'apprendre qu'il est inutile.
  */
  const cleanRoads: ButtonHandle = createButton({
    label: t('city.cleanRoads'),
    variant: 'secondary',
    icon: 'road',
    onClick: callbacks.onCleanRoads,
  });
  cleanRoads.el.classList.add('city-clean');
  cleanRoads.el.hidden = true;

  const landActions = document.createElement('div');
  landActions.className = 'city-land__actions';
  landActions.append(buyLand.el, buyLandGems.el, cleanRoads.el);

  const landPanel = createPanel({
    children: [
      (() => {
        const head = document.createElement('h3');
        head.className = 'city-section__title';
        head.append(icon('city'), t('city.landTitle'));
        return head;
      })(),
      size.el,
      expansions.el,
      landActions,
      landPrice,
    ],
  });

  /* ── Cadrage ──────────────────────────────────────────────────────────── */
  const zoomIn = createIconButton({
    icon: 'plus',
    label: t('city.zoomIn'),
    onClick: callbacks.onZoomIn,
  });
  const zoomOut = createIconButton({
    icon: 'minus',
    label: t('city.zoomOut'),
    onClick: callbacks.onZoomOut,
  });
  const recenter = createIconButton({
    // `focus` et non `eye` : l'œil dit « regarder », pas « recadrer » — une icône = un
    // sens (D§6), et l'œil sert déjà aux visites.
    icon: 'focus',
    label: t('city.recenter'),
    onClick: callbacks.onRecenter,
  });

  // La rotation de vue est le remède standard à l'occultation en isométrie : un grand
  // bâtiment en cache d'autres, et rien d'autre ne permet de voir derrière.

  const controls = document.createElement('div');
  controls.className = 'city-controls';
  controls.append(zoomOut.el, recenter.el, zoomIn.el);

  const viewPanel = createPanel({
    children: [
      (() => {
        const head = document.createElement('h3');
        head.className = 'city-section__title';
        head.append(icon('eye'), t('city.viewTitle'));
        return head;
      })(),
      controls,
    ],
  });

  /*
    ── Ce qui arrive — les places réservées (D§4) ────────────────────────────

    La météo réelle (§16.2) et la capsule temporelle (§16.5) ont leur drapeau et leur
    place dans cette section. D§4 exige qu'un vide raconte : deux cartes scellées
    annoncent ce qu'on y trouvera — l'attente fait partie du jeu, pas du chantier.
  */
  const sealed = document.createElement('div');
  sealed.className = 'city-sealed';
  for (const key of ['weather', 'capsule'] as const) {
    const card = document.createElement('div');
    card.className = 'city-sealed__card';

    const name = document.createElement('b');
    name.textContent = t(`city.sealed.${key}.title` as TranslationKey);

    const text = document.createElement('span');
    text.textContent = t(`city.sealed.${key}.text` as TranslationKey);

    card.append(icon(key === 'weather' ? 'flower' : 'clock', { size: 'large' }), name, text);
    sealed.append(card);
  }

  /*
    ══ LA SECTION VILLE EN ONGLETS (ROADMAP 3.1, demande du 20 août) ══════════

    Cinq panneaux s'empilaient : identité, statistiques, terrain, cadrage, et les deux
    cartes scellées. Le découpage suit ce que le joueur VIENT y faire, pas l'ordre dans
    lequel le code les a créés :

    · **Résumé** — qui est cette ville : son nom, ses chiffres, ce qui l'attend ;
    · **Terrain** — l'agrandir, et récupérer le bitume inutile (5.2) ;
    · **Vue** — zoom et recadrage, les seuls réglages qu'on touche en jouant.

    Trois onglets et non cinq : un onglet par panneau rendrait la barre plus longue que
    ce qu'elle range. On regroupe ce qui se consulte, on isole ce qui s'actionne.
  */
  const tabs = createTabbedSection([
    {
      id: 'summary',
      label: t('city.tabSummary'),
      icon: 'city',
      content: [identityPanel.el, statsPanel.el, sealed],
    },
    {
      id: 'land',
      label: t('city.tabLand'),
      icon: 'plus',
      content: [landPanel.el],
    },
    {
      id: 'view',
      label: t('city.tabView'),
      icon: 'eye',
      content: [viewPanel.el],
    },
  ]);

  el.append(tabs.el);

  return {
    el,
    update(snapshot) {
      const island = activeIsland(snapshot);
      size.set(`${String(island.grid.cols)} × ${String(island.grid.rows)}`);
      expansions.set(`${String(snapshot.landPurchases)} / ${String(ECONOMY.land.maxPurchases)}`);

      const maxed = snapshot.landPurchases >= ECONOMY.land.maxPurchases;
      const cost = nextLandCost(snapshot.landPurchases);

      /*
        Le compte se demande au jeu, qui SIMULE le geste. C'est plus cher qu'une
        estimation, et c'est le prix d'un chiffre qui ne ment pas — la section n'est
        rafraîchie que lorsqu'elle est à l'écran.
      */
      const orphans = callbacks.orphanRoads();
      cleanRoads.el.hidden = orphans === 0;
      if (orphans > 0) {
        cleanRoads.setLabel(t('city.cleanRoadsFor', { count: orphans }));
      }

      buyLand.setDisabled(maxed || snapshot.coins < cost);
      // Le prix reste affiché même hors de portée : c'est l'objectif suivant, pas une
      // information à cacher.
      landPrice.textContent = maxed ? t('city.landMaxed') : t('city.landCost', { value: n(cost) });

      /*
        Le bouton gemmes disparaît au plafond des douze extensions — il ne se contente pas
        d'être grisé. Une monnaie ne rachète pas une règle de conception (§6), et laisser
        l'offre visible sur un plafond structurel promettrait le contraire.
      */
      buyLandGems.el.hidden = maxed;
      buyLandGems.setDisabled(snapshot.gems < GEM_SPENDING.land.cost);

      statBuildings.set(n(island.buildings.length));
      statRoads.set(n(island.roads.length));
      statProduction.set(t('units.perSecond', { value: n(currentProduction(snapshot)) }));
      statVisitors.set(n(snapshot.visitorsReceived));
      statEra.set(
        t('city.eraValue', {
          value: String(snapshot.prestigeLevel + 1),
          multiplier: n(snapshot.prestigeMultiplier),
        }),
      );
    },
    setCity(city) {
      const known = city !== null;
      renameButton.setDisabled(!known);
      if (city === null) return;
      // En édition, on ne remplace pas ce que le joueur est en train de taper.
      if (renameInput.hidden) cityName.textContent = city.name;
      citySlug.textContent = `villopia.app/ville/${city.slug}`;
      citySlug.hidden = false;
    },
  };
}
